import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

const credentials = [
  {
    kind: "Degree",
    title: "Master of Data Science and Innovation",
    provider: "University of Technology Sydney",
    year: "2023 - 2025",
    type: "Education",
    focus:
      "Advanced training in data science, analytics, machine learning, innovation, and applied technical problem solving.",
    seoKeywords: [
      "Master of Data Science and Innovation",
      "University of Technology Sydney",
      "UTS alumni",
      "data science degree",
      "machine learning",
      "analytics",
      "artificial intelligence",
    ],
    file: "/certificates/uts.pdf",
    featured: true,
  },
  {
    kind: "Degree",
    title: "Machine Learning Exchange Program",
    provider: "University of California Davis",
    year: "2022",
    type: "Education",
    focus:
      "Academic exchange work focused on machine learning, applied modeling, and international technical study exposure.",
    seoKeywords: [
      "Machine Learning Exchange Program",
      "University of California Davis",
      "UC Davis",
      "machine learning program",
      "international exchange",
      "data science",
    ],
    file: "/certificates/ucd.pdf",
    featured: true,
  },
  {
    kind: "Degree",
    title: "Bachelor of Technology",
    provider: "SRM Institute of Science and Technology",
    year: "2018 - 2022",
    type: "Education",
    focus:
      "Undergraduate engineering and technical foundation covering computing, software, systems, and analytical thinking.",
    seoKeywords: [
      "Bachelor of Technology",
      "SRM Institute of Science and Technology",
      "SRMIST",
      "engineering degree",
      "technology degree",
      "software engineering",
      "computer science",
    ],
    file: "/certificates/srm.pdf",
    featured: true,
  },

  {
    kind: "Certificate",
    title: "Building Web Applications in PHP",
    provider: "Coursera / University of Michigan",
    year: "2021",
    type: "Web",
    focus:
      "Practical web application development and software implementation fundamentals.",
    seoKeywords: [
      "web development certificate",
      "php",
      "software development",
      "university of michigan",
    ],
    file: "/certificates/Building Web Applications in PHP.pdf",
  },
  {
    kind: "Certificate",
    title: "Artificial Intelligence Foundations: Thinking Machines",
    provider: "LinkedIn Learning",
    year: "2019",
    type: "AI/ML",
    focus:
      "Foundational learning in artificial intelligence concepts, machine reasoning, and technical thinking.",
    seoKeywords: [
      "artificial intelligence certificate",
      "ai foundations",
      "machine reasoning",
      "machine learning",
    ],
    file: "/certificates/CertificateOfCompletion_Artificial Intelligence Foundations_ Thinking Machines.pdf",
  },
  {
    kind: "Certificate",
    title: "Cloud Architecture: Core Concepts",
    provider: "LinkedIn Learning",
    year: "2019",
    type: "Cloud",
    focus:
      "Core concepts around cloud systems, architecture patterns, and distributed infrastructure thinking.",
    seoKeywords: [
      "cloud architecture certificate",
      "cloud computing",
      "distributed systems",
      "infrastructure",
    ],
    file: "/certificates/CertificateOfCompletion_Cloud Architecture Core Concepts 2.pdf",
  },
  {
    kind: "Certificate",
    title: "Learning Cloud Computing: Public Cloud Platforms",
    provider: "LinkedIn Learning",
    year: "Unknown",
    type: "Cloud",
    focus:
      "Public cloud fundamentals and platform-level cloud delivery concepts.",
    seoKeywords: [
      "cloud computing certificate",
      "public cloud",
      "cloud platforms",
      "infrastructure",
    ],
    file: "/certificates/CertificateOfCompletion_Learning Cloud Computing_ Public Cloud Platforms.pdf",
  },
  {
    kind: "Certificate",
    title: "Networking Foundations: Servers",
    provider: "LinkedIn Learning",
    year: "2021",
    type: "Infrastructure",
    focus:
      "Learning around server fundamentals, networking concepts, and technical infrastructure basics.",
    seoKeywords: [
      "networking certificate",
      "servers",
      "infrastructure",
      "systems",
    ],
    file: "/certificates/CertificateOfCompletion_Networking Foundations Servers.pdf",
  },
  {
    kind: "Certificate",
    title: "Project Management Institute (PMI)",
    provider: "LinkedIn Learning / PMI",
    year: "2020",
    type: "Professional",
    focus:
      "Professional development focused on planning, structured thinking, and project-oriented execution.",
    seoKeywords: [
      "project management certificate",
      "professional development",
      "PMI",
      "delivery",
    ],
    file: "/certificates/Project Management Institute (PMI).pdf",
  },
];

const pdfPreviewUrl = file =>
  `${file}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`;

const getCredentialText = item => {
  if (item.focus) return item.focus;

  const descriptions = {
    Education:
      "Formal academic training supporting software engineering, data science, machine learning, analytics, and technical systems thinking.",
    "AI/ML":
      "Foundational learning in artificial intelligence concepts, machine reasoning, and technical thinking.",
    Cloud:
      "Core concepts around cloud systems, architecture patterns, and distributed infrastructure thinking.",
    Professional:
      "Professional development focused on planning, structured thinking, and project-oriented execution.",
    Infrastructure:
      "Learning around server fundamentals, networking concepts, and technical infrastructure basics.",
    Design:
      "Design principles that improve communication, usability, and visual decision-making.",
    Web:
      "Coursework focused on application building and practical web development fundamentals.",
  };

  return (
    descriptions[item.type] ||
    "Formal learning and technical coursework supporting continued professional development."
  );
};

const StyledCredentialsPage = styled.main`
  width: 100%;
`;

const HeroSection = styled.section`
  max-width: 1260px;
  margin: 0 auto;
  padding: 170px clamp(28px, 6vw, 80px) 70px;

  .eyebrow {
    margin: 0 0 18px;
    color: var(--text-muted);
    font-family: var(--font-mono);
    font-size: 13px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
  }

  h1 {
    margin: 0 0 22px;
    color: var(--text-primary);
    font-size: clamp(42px, 6vw, 82px);
    line-height: 0.98;
    letter-spacing: -0.05em;
    max-width: 900px;
  }

  .lead {
    margin: 0;
    max-width: 820px;
    color: var(--text-secondary);
    font-size: clamp(18px, 2vw, 23px);
    line-height: 1.8;
  }

  .lead strong {
    color: var(--text-primary);
    font-weight: 600;
  }
`;

const EducationHighlights = styled.section`
  max-width: 1260px;
  margin: 0 auto;
  padding: 0 clamp(28px, 6vw, 80px) 34px;

  .top {
    display: grid;
    grid-template-columns: 1fr;
    gap: 14px;
    margin-bottom: 20px;
  }

  .label {
    margin: 0;
    color: var(--text-muted);
    font-family: var(--font-mono);
    font-size: 13px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
  }

  .copy {
    margin: 0;
    max-width: 860px;
    color: var(--text-secondary);
    font-size: 17px;
    line-height: 1.9;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;

    @media (max-width: 860px) {
      grid-template-columns: 1fr;
    }
  }

  .card {
    padding: 24px;
    border-radius: 26px;
    background: rgba(255, 255, 255, 0.42);
    border: 1px solid rgba(255, 255, 255, 0.55);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
    box-shadow:
      0 10px 30px rgba(0, 0, 0, 0.04),
      inset 0 1px 0 rgba(255, 255, 255, 0.35);
  }

  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 14px;
  }

  .pill {
    display: inline-flex;
    align-items: center;
    padding: 7px 10px;
    border-radius: 999px;
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-secondary);
    background: rgba(255, 255, 255, 0.62);
    border: 1px solid var(--border-light);
  }

  h2 {
    margin: 0 0 12px;
    color: var(--text-primary);
    font-size: clamp(28px, 3vw, 38px);
    line-height: 1.12;
    letter-spacing: -0.035em;
  }

  p {
    margin: 0;
    color: var(--text-secondary);
    font-size: 15px;
    line-height: 1.85;
  }
`;

const FiltersSection = styled.section`
  max-width: 1260px;
  margin: 0 auto;
  padding: 0 clamp(28px, 6vw, 80px) 26px;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const Chip = styled.button`
  padding: 9px 14px;
  border-radius: 999px;
  border: 1px solid
    ${({ $active }) =>
      $active ? "rgba(17,17,17,0.18)" : "var(--border-light)"};
  background: ${({ $active }) =>
    $active ? "rgba(255,255,255,0.72)" : "rgba(255,255,255,0.38)"};
  color: ${({ $active }) =>
    $active ? "var(--text-primary)" : "var(--text-secondary)"};
  font-family: var(--font-mono);
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  transition: var(--transition);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);

  &:hover {
    transform: translateY(-1px);
    border-color: var(--border-medium);
  }
`;

const ContentSection = styled.section`
  max-width: 1260px;
  margin: 0 auto;
  padding: 0 clamp(28px, 6vw, 80px) 150px;

  .layout {
    display: grid;
    grid-template-columns: 1.05fr 0.95fr;
    gap: 20px;
    align-items: start;

    @media (max-width: 1100px) {
      grid-template-columns: 1fr;
    }
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.button`
  width: 100%;
  text-align: left;
  padding: 22px;
  border-radius: 24px;
  border: 1px solid
    ${({ $active }) =>
      $active ? "rgba(17,17,17,0.16)" : "rgba(255,255,255,0.55)"};
  background: ${({ $active }) =>
    $active ? "rgba(255,255,255,0.68)" : "rgba(255,255,255,0.42)"};
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  box-shadow:
    0 10px 30px rgba(0, 0, 0, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.35);
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    transform: translateY(-2px);
    box-shadow:
      0 14px 34px rgba(0, 0, 0, 0.06),
      inset 0 1px 0 rgba(255, 255, 255, 0.42);
  }
`;

const CardMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 10px;
  border-radius: 999px;
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--text-secondary);
  background: rgba(255, 255, 255, 0.62);
  border: 1px solid var(--border-light);
`;

const TypePill = styled(Pill)`
  color: var(--text-primary);
`;

const KindPill = styled(Pill)`
  color: #ffffff;
  background: #111111;
  border-color: #111111;
`;

const CardTitle = styled.h2`
  margin: 0 0 12px;
  color: var(--text-primary);
  font-size: 24px;
  line-height: 1.18;
  letter-spacing: -0.03em;
`;

const CardText = styled.p`
  margin: 0;
  color: var(--text-secondary);
  font-size: 15px;
  line-height: 1.85;
`;

const KeywordRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
`;

const Keyword = styled.span`
  padding: 6px 10px;
  border-radius: 999px;
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--text-secondary);
  background: rgba(255, 255, 255, 0.48);
  border: 1px solid var(--border-light);
`;

const PreviewPanel = styled.div`
  position: sticky;
  top: calc(var(--nav-height) + 18px);
  padding: 22px;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.42);
  border: 1px solid rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  box-shadow:
    0 12px 34px rgba(0, 0, 0, 0.045),
    inset 0 1px 0 rgba(255, 255, 255, 0.36);

  @media (max-width: 1100px) {
    position: relative;
    top: auto;
  }
`;

const PreviewMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
`;

const PreviewTitle = styled.h2`
  margin: 0 0 14px;
  color: var(--text-primary);
  font-size: clamp(28px, 3vw, 38px);
  line-height: 1.1;
  letter-spacing: -0.035em;
`;

const PreviewText = styled.p`
  margin: 0;
  color: var(--text-secondary);
  font-size: 16px;
  line-height: 1.85;
`;

const PreviewActions = styled.div`
  margin-top: 20px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 46px;
  padding: 0 18px;
  border-radius: 999px;
  text-decoration: none;
  font-family: var(--font-mono);
  font-size: 13px;
  transition: var(--transition);
  cursor: pointer;
  color: #ffffff;
  background: #111111;
  border: 1px solid #111111;

  &:hover {
    background: #2a2a2a;
    border-color: #2a2a2a;
    transform: translateY(-1px);
  }
`;

const SecondaryLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 46px;
  padding: 0 18px;
  border-radius: 999px;
  text-decoration: none;
  font-family: var(--font-mono);
  font-size: 13px;
  transition: var(--transition);
  color: var(--text-primary);
  background: rgba(255, 255, 255, 0.58);
  border: 1px solid var(--border-light);

  &:hover {
    color: var(--text-primary);
    border-color: var(--border-medium);
    background: rgba(255, 255, 255, 0.78);
    transform: translateY(-1px);
  }
`;

const PreviewShell = styled.div`
  margin-top: 22px;
  border-radius: 22px;
  overflow: hidden;
  border: 1px solid var(--border-light);
  background: rgba(255, 255, 255, 0.52);
  min-height: 520px;
  box-shadow:
    0 10px 30px rgba(0, 0, 0, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.35);

  @media (max-width: 760px) {
    min-height: 420px;
  }
`;

const PreviewFrame = styled.iframe`
  width: 100%;
  height: 520px;
  border: 0;
  background: #ffffff;

  @media (max-width: 760px) {
    height: 420px;
  }
`;

const EmptyState = styled.div`
  padding: 20px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.42);
  border: 1px solid var(--border-light);
  color: var(--text-secondary);
  font-size: 15px;
  line-height: 1.8;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(20, 20, 20, 0.28);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;

  @media (max-width: 560px) {
    padding: 0;
  }
`;

const ModalCard = styled.div`
  width: min(1180px, 100%);
  height: min(90vh, 920px);
  border-radius: 26px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.55);
  background: rgba(255, 255, 255, 0.78);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  box-shadow: 0 26px 70px rgba(0, 0, 0, 0.14);
  display: flex;
  flex-direction: column;

  @media (max-width: 560px) {
    width: 100%;
    height: 100vh;
    border-radius: 0;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  border-bottom: 1px solid var(--border-light);
  background: rgba(255, 255, 255, 0.58);
`;

const ModalTitleWrap = styled.div`
  min-width: 0;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: var(--text-primary);
  font-size: 18px;
  line-height: 1.35;
`;

const ModalMeta = styled.div`
  margin-top: 6px;
  color: var(--text-secondary);
  font-size: 12px;
  font-family: var(--font-mono);
`;

const CloseBtn = styled.button`
  width: 42px;
  height: 42px;
  border-radius: 999px;
  border: 1px solid var(--border-light);
  background: rgba(255, 255, 255, 0.65);
  color: var(--text-primary);
  font-size: 18px;
  cursor: pointer;
  transition: var(--transition);
  flex-shrink: 0;

  &:hover {
    border-color: var(--border-medium);
    background: rgba(255, 255, 255, 0.9);
  }
`;

const ModalFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: 0;
  background: #ffffff;
`;

const CredentialsPageContent = () => {
  const [activeType, setActiveType] = useState("All");
  const [selectedItem, setSelectedItem] = useState(credentials[0]);
  const [modalOpen, setModalOpen] = useState(false);

  const categories = useMemo(
    () => [
      "All",
      "Degree",
      "Certificate",
      ...Array.from(new Set(credentials.map(item => item.type))),
    ],
    []
  );

  const featuredDegrees = useMemo(
    () => credentials.filter(item => item.kind === "Degree" && item.featured),
    []
  );

  const filteredItems = useMemo(() => {
    if (activeType === "All") return credentials;
    if (activeType === "Degree" || activeType === "Certificate") {
      return credentials.filter(item => item.kind === activeType);
    }
    return credentials.filter(item => item.type === activeType);
  }, [activeType]);

  useEffect(() => {
    if (!filteredItems.find(item => item.title === selectedItem?.title)) {
      setSelectedItem(filteredItems[0] || null);
    }
  }, [filteredItems, selectedItem]);

  useEffect(() => {
    document.body.style.overflow = modalOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [modalOpen]);

  const activeItem = selectedItem || filteredItems[0];

  return (
    <StyledCredentialsPage>
      <HeroSection>
        <p className="eyebrow">Credentials</p>
        <h1>Academic background, university affiliations, and technical credentials.</h1>
        <p className="lead">
          A structured record of <strong>degrees, exchange study, university affiliations, technical certifications, machine learning coursework, software engineering foundations, and data science education</strong> supporting my work across software, analytics, machine learning, and AI.
        </p>
      </HeroSection>

      <EducationHighlights>
        <div className="top">
          <p className="label">Education</p>
          <p className="copy">
            This section combines <strong>formal academic education</strong> with
            <strong> technical certificates and continuing learning</strong>. It highlights my
            background across <strong>University of Technology Sydney, University of California Davis, and SRM Institute of Science and Technology</strong>,
            alongside applied coursework in software, machine learning, cloud, and infrastructure.
          </p>
        </div>

        <div className="grid">
          {featuredDegrees.map(item => (
            <div className="card" key={`${item.title}-${item.provider}`}>
              <div className="meta">
                <span className="pill">{item.kind}</span>
                <span className="pill">{item.provider}</span>
                <span className="pill">{item.year}</span>
              </div>
              <h2>{item.title}</h2>
              <p>{getCredentialText(item)}</p>
            </div>
          ))}
        </div>
      </EducationHighlights>

      <FiltersSection>
        <Chips>
          {categories.map(category => (
            <Chip
              key={category}
              type="button"
              $active={activeType === category}
              onClick={() => setActiveType(category)}
            >
              {category}
            </Chip>
          ))}
        </Chips>
      </FiltersSection>

      <ContentSection>
        {activeItem ? (
          <div className="layout">
            <div>
              {filteredItems.length ? (
                <Grid>
                  {filteredItems.map(item => (
                    <Card
                      key={`${item.title}-${item.provider}-${item.year}`}
                      type="button"
                      $active={activeItem?.title === item.title}
                      onClick={() => setSelectedItem(item)}
                    >
                      <CardMeta>
                        <KindPill>{item.kind}</KindPill>
                        <Pill>{item.provider}</Pill>
                        <Pill>{item.year}</Pill>
                        <TypePill>{item.type}</TypePill>
                      </CardMeta>

                      <CardTitle>{item.title}</CardTitle>
                      <CardText>{getCredentialText(item)}</CardText>

                      {item.seoKeywords?.length ? (
                        <KeywordRow>
                          {item.seoKeywords.map(keyword => (
                            <Keyword key={keyword}>{keyword}</Keyword>
                          ))}
                        </KeywordRow>
                      ) : null}
                    </Card>
                  ))}
                </Grid>
              ) : (
                <EmptyState>
                  No items found for this category.
                </EmptyState>
              )}
            </div>

            <PreviewPanel>
              <PreviewMeta>
                <KindPill>{activeItem.kind}</KindPill>
                <Pill>{activeItem.provider}</Pill>
                <Pill>{activeItem.year}</Pill>
                <TypePill>{activeItem.type}</TypePill>
              </PreviewMeta>

              <PreviewTitle>{activeItem.title}</PreviewTitle>
              <PreviewText>{getCredentialText(activeItem)}</PreviewText>

              {activeItem.seoKeywords?.length ? (
                <KeywordRow>
                  {activeItem.seoKeywords.map(keyword => (
                    <Keyword key={keyword}>{keyword}</Keyword>
                  ))}
                </KeywordRow>
              ) : null}

              <PreviewActions>
                <ActionButton type="button" onClick={() => setModalOpen(true)}>
                  Open preview
                </ActionButton>

                <SecondaryLink
                  href={activeItem.file}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open document
                </SecondaryLink>
              </PreviewActions>

              <PreviewShell>
                <PreviewFrame
                  src={pdfPreviewUrl(activeItem.file)}
                  title={`${activeItem.title} preview`}
                />
              </PreviewShell>
            </PreviewPanel>
          </div>
        ) : (
          <EmptyState>No credentials available.</EmptyState>
        )}
      </ContentSection>

      {modalOpen && activeItem && (
        <ModalOverlay
          onClick={() => setModalOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label={`${activeItem.title} document`}
        >
          <ModalCard onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitleWrap>
                <ModalTitle>{activeItem.title}</ModalTitle>
                <ModalMeta>
                  {activeItem.kind} • {activeItem.provider} • {activeItem.year} • {activeItem.type}
                </ModalMeta>
              </ModalTitleWrap>

              <CloseBtn
                type="button"
                onClick={() => setModalOpen(false)}
                aria-label="Close document"
              >
                ✕
              </CloseBtn>
            </ModalHeader>

            <ModalFrame
              src={pdfPreviewUrl(activeItem.file)}
              title={`${activeItem.title} full document`}
            />
          </ModalCard>
        </ModalOverlay>
      )}
    </StyledCredentialsPage>
  );
};

export default CredentialsPageContent;